import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Loader2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DocumentFileLink } from './DocumentFileLink';
import { acceptedFiles, MAX_DOCUMENT_MB } from './documentUploadConfig';
import { useDocumentUpload } from './useDocumentUpload';

export const DocumentUploadField = ({ config, urls, onUploaded, children }) => {
  const { t } = useTranslation();
  const input = useRef(null);
  const { state, upload } = useDocumentUpload(config, onUploaded);
  const busy = state.status === 'uploading';
  const id = `candidate-document-${config.type}`;
  const label = config.type === 'cv' ? 'CV' : t(`candidateProfile.${config.label}`);
  const formats = t('candidateProfile.upload.formatSummary');
  const statusText = busy
    ? t(`candidateProfile.upload.${state.progress === 100 ? 'storing' : 'sending'}`, { progress: state.progress })
    : state.status === 'success' ? t('candidateProfile.upload.saved', { name: state.name }) : '';

  return (
    <div className={`min-w-0 border-2 border-dashed rounded-lg p-4 ${config.recommended ? 'border-blue-300 bg-blue-50/30' : 'border-slate-200'}`} data-testid={id}>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <h4 id={`${id}-label`} className="text-sm font-medium text-slate-900 break-words" data-testid={`${id}-label`}>{label}</h4>
        <span className={`text-xs px-2 py-0.5 rounded-full ${config.recommended ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`} data-testid={`${id}-optional`}>
          {t(`candidateProfile.${config.recommended ? 'recommended' : 'optional'}`)}
        </span>
        {urls.length > 0 && <CheckCircle className="h-4 w-4 text-green-700" aria-hidden="true" />}
      </div>
      <input ref={input} type="file" className="sr-only" tabIndex={-1} aria-labelledby={`${id}-label`} aria-describedby={`${id}-formats`}
        accept={acceptedFiles(config.extensions)} disabled={busy} data-testid={`${id}-input`}
        onChange={event => { const file = event.target.files?.[0]; event.target.value = ''; upload(file); }} />
      <Button type="button" variant="outline" disabled={busy} onClick={() => input.current?.click()}
        className="w-full min-h-11 h-auto py-2 whitespace-normal gap-2 text-left" aria-label={`${t('candidateProfile.upload.choose')} — ${label}`} data-testid={`${id}-choose`}>
        {busy ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" /> : <Upload className="h-4 w-4 shrink-0" />}
        {t(`candidateProfile.upload.${busy ? 'sendingShort' : urls.length && !config.multiple ? 'replace' : 'choose'}`)}
      </Button>
      <p id={`${id}-formats`} className="mt-2 text-xs leading-relaxed text-slate-500" data-testid={`${id}-formats`}>
        {t('candidateProfile.upload.formats', { formats, max: MAX_DOCUMENT_MB })}
      </p>
      {busy && <progress max="100" value={state.progress} aria-label={label} className="mt-2 w-full h-2 accent-blue-600" data-testid={`${id}-progress`} />}
      <div aria-live="polite" role="status" className="text-sm break-words [overflow-wrap:anywhere]" data-testid={`${id}-status`}>
        {statusText && <p className={`mt-2 ${busy ? 'text-blue-700' : 'text-green-700'}`}>{statusText}</p>}
      </div>
      {state.error && <p role="alert" className="mt-2 text-sm text-red-700 break-words [overflow-wrap:anywhere]" data-testid={`${id}-error`}>
        {t(`candidateProfile.upload.errors.${state.error}`, { max: MAX_DOCUMENT_MB, formats })}
      </p>}
      {urls.length > 0 && <ul className="mt-3 space-y-1" data-testid={`${id}-files`}>
        {urls.map((url, index) => {
          const fileLabel = config.multiple ? t('candidateProfile.upload.numberedFile', { count: index + 1 }) : label;
          return <li key={`${url}-${index}`} className="min-w-0">
            <DocumentFileLink url={url} label={fileLabel} testId={`${id}-view-${index}`} />
          </li>;
        })}
      </ul>}
      {children}
    </div>
  );
};